import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetSalaryTypes } from 'src/hooks/salary-types/get-salary-types.hook';
import { useDeleteSalaryTypesById } from 'src/hooks/salary-types/delete-salary-types-by-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer/';
import { SalaryTypesTable } from './components/salary-types-table';
import { ChangeSalaryTypes } from './components/change-salary-types';

import { SALARY_TYPES_MODALS, DEFAULT_SALARY_TYPE } from './salary-types.constants';

import { SalaryTypesWrapper } from './salary-types.styles';

const SalaryTypes = () => {
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [salaryType, setSalaryType] = useState(DEFAULT_SALARY_TYPE);

  const currentCompanyId = useGetCurrentCompanyId();
  const { collection, paginationConfig, onTableChange, loading } = useGetSalaryTypes(
    currentCompanyId
  );

  const handleCreateSalaryType = () => {
    setActiveModal(SALARY_TYPES_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSalaryType(DEFAULT_SALARY_TYPE);
  };

  const deleteSalaryTypeById = useDeleteSalaryTypesById({
    onSuccess: closeModal,
  });

  const deleteSalaryType = () => {
    deleteSalaryTypeById({
      variables: { id: salaryType?._id },
    });
    setSalaryType(DEFAULT_SALARY_TYPE);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteSalaryType();
  };

  const isCreate = activeModal === SALARY_TYPES_MODALS.create;
  const isEdit = activeModal === SALARY_TYPES_MODALS.edit;

  return (
    <SalaryTypesWrapper>
      <DrawerComponent
        title={
          (t(`salaryTypes.labels.${isCreate ? 'addNewSalaryType' : 'editSalaryType'}`))
        }
        subtitle={t(
          `salaryTypes.labels.${isCreate ? 'addNewSalaryTypeSubtitle' : 'editSalaryTypeSubtitle'}`, {
            name: salaryType?.name,
          })}
        onClose={closeModal}
        visible={isEdit || isCreate}
        destroyOnClose
      >
        <ChangeSalaryTypes onClose={closeModal} id={salaryType._id} />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === SALARY_TYPES_MODALS.delete}
        title={t('salaryTypes.deleteModal.title')}
        subTitle={t('salaryTypes.deleteModal.subtitle', {
          name: salaryType.name,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('salaryTypes.labels.salaryTypes')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.companySettings}
          action={PERMISSION_ACTIONS.create}
        >
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={handleCreateSalaryType}
            >
              {t('salaryTypes.buttons.addSalaryType')}
            </Button>
          </ButtonsWrapper>
        </PermissionCheck>
      </div>
      <SalaryTypesTable
        dataSource={collection}
        paginationConfig={paginationConfig}
        onTableChange={onTableChange}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setSalaryType(record);
        }}
        loading={loading}
      />
    </SalaryTypesWrapper>
  );
};

export default SalaryTypes;
